// components/VisualisationBDC.tsx
// Visualisation des bons de caisse — même présentation que
// /assurance/visualisation (Safe Care) :
//   critères de recherche -> liste des bons -> clic sur une ligne ->
//   fiche à onglets (Résumé / Échéancier / Frais / Contrat signé).
import React, { useEffect, useMemo, useState } from "react";
import { Button, Descriptions, Form, Input, Select, Space, Spin, Table, Tabs, Tag, Modal } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import {
  Typography, Box,
} from "@mui/material";
import axios from "axios";
import { IBonDeCaisse } from "../../../dataTypes/GlobalDataType";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord, normalizeKeysToUppercase } from "../../../utilitaires/globalFunction";
import { normalizeListToUpperKeys, parseEncryptedResponse } from "../../Mfa/mfaApi";
import ExportMenu from "../../ExportMenu";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";

interface IEcheance {
  periode: number;
  dateImput: string;
  interet: number;
  taxe: number;
  netAPayer: number;
  statut?: string;   // libellé : 'En attente' | 'Prélevé' | 'Annulée'
  datPrel?: string;  // date de prélèvement (ECPIMPUTPROVBDC.DATPRELEV)
}

const ETAT_COLOR: Record<string, string> = {
  '01': 'orange', '02': 'green', '03': 'red', '04': 'default',
};
const ETAT_LABEL: Record<string, string> = {
  '01': 'En cours', '02': 'Validé', '03': 'Rejeté', '04': 'Échu',
};
const libEtat = (s?: string) => (s ? ETAT_LABEL[s] ?? s : '');
const EtatTag = ({ s }: { s?: string }) => (s ? <Tag color={ETAT_COLOR[s] ?? 'default'}>{libEtat(s)}</Tag> : null);
const fmtDate = (d?: any) => (d ? new Date(d).toLocaleDateString() : '');
const fmtDateTime = (d?: any) => (d ? new Date(d).toLocaleString() : '');

export default function VisualisationBDC() {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState<IBonDeCaisse[]>([]);
  const [typesBdc, setTypesBdc] = useState<{ CODBDC: string; LIBELL: string }[]>([]);
  const [usersByCode, setUsersByCode] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<IBonDeCaisse | null>(null);
  const [ficheTab, setFicheTab] = useState('resume');

  // Critères
  const [recherche, setRecherche] = useState('');
  const [etat, setEtat] = useState<string | undefined>(undefined);
  const [typBdc, setTypBdc] = useState<string | undefined>(undefined);

  // Fiche
  const [echeancier, setEcheancier] = useState<IEcheance[]>([]);
  const [frais, setFrais] = useState<any[]>([]);
  const [contratB64, setContratB64] = useState<string>('');
  const [contratNom, setContratNom] = useState<string>('');
  const [contratPreview, setContratPreview] = useState(false);

  const totalInteret = echeancier.reduce((acc, r) => acc + r.interet, 0);
  const totalTaxe = echeancier.reduce((acc, r) => acc + r.taxe, 0);
  const totalNet = echeancier.reduce((acc, r) => acc + r.netAPayer, 0);

  // ------------------------------------------------------------------
  // Chargement
  // ------------------------------------------------------------------
  const fetchTypes = async () => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setTypesBdc(res || []);
    } catch (err) { console.warn('fetchTypes - erreur', err); }
  };

  // Annuaire CODUTI -> NOMUTI (sp_Utilisateur_GetAll via MfaComplement,
  // endpoint confirmé utilisé par MfaAdminPanel) pour afficher le nom de
  // l'utilisateur plutôt que son code.
  const fetchUsers = async () => {
    try {
      const res = await axios.get(Data.MfaApiUrl.mfaUsersAll, {
        headers: { Authorization: authHeader(), 'Content-Type': 'application/json' },
      });
      const parsed = parseEncryptedResponse(res);
      const rows = normalizeListToUpperKeys<any>(parsed?.Result ?? parsed?.result ?? parsed);
      const map: Record<string, string> = {};
      rows.forEach((u: any) => {
        if (!u.CODUTI) return;
        const code = String(u.CODUTI).trim();
        const nom = u.NOMUTI || code;
        map[code] = nom;
        if (!isNaN(Number(code))) map[String(Number(code))] = nom;
      });
      setUsersByCode(map);
    } catch (err) { console.warn('fetchUsers - erreur', err); }
  };
  const libUti = (code?: any) => {
    if (!code) return '';
    const s = String(code).trim();
    return usersByCode[s] ?? usersByCode[String(Number(s))] ?? s;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      var params = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setAllData(res || []);
    } catch (err) {
      console.error("Erreur de chargement:", err);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); fetchTypes(); fetchUsers(); }, []);

  // Filtrage local (état / type / recherche libre)
  const filtered = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return allData.filter((b: any) => {
      if (etat && b.CDETAT !== etat) return false;
      if (typBdc && b.TYPBDC !== typBdc) return false;
      if (!q) return true;
      return [b.NUMBDC, b.REFBDC, b.CODSIMUL, b.NUMCLI, b.NOMCLI, b.CPTDEPOT, b.CPTBDC]
        .some((v) => (v ?? '').toString().toLowerCase().includes(q));
    });
  }, [allData, recherche, etat, typBdc]);

  // ------------------------------------------------------------------
  // Fiche : échéancier calculé + frais + contrat
  // ------------------------------------------------------------------
  const joursFreq = (freq: string) => {
    switch ((freq || "").toUpperCase()) {
      case "A": return 360; case "S": return 180; case "T": return 90;
      case "M": return 30; case "B": return 15; case "F": return 60;
      case "H": return 7; case "J": return 1; default: return 30;
    }
  }

  const calculerEcheancier = (row: IBonDeCaisse) => {
    const jours = joursFreq(row.FREQPROV || "");
    const montant = Number(row.MONTANT) || 0;
    const duree = Number(row.DUREE) || 0;
    const montIntBrut = (montant * (Number(row.TXINT) || 0) * duree * jours) / 36000;
    const montProvFreq = Math.round(montIntBrut / duree);
    const montTaxeBrut = ((montProvFreq * duree) * (Number(row.TXTAXE) || 0)) / 100;
    const montTaxeFreq = Math.round(montTaxeBrut / duree);

    let dateEcheance = new Date(row.DATDEBUT || Perss.datjour);
    const list: IEcheance[] = [];
    for (let i = 1; i <= duree; i++) {
      const prochaine = new Date(dateEcheance);
      prochaine.setDate(prochaine.getDate() + jours);
      list.push({
        periode: i,
        dateImput: dateEcheance.toLocaleDateString(),
        interet: montProvFreq,
        taxe: montTaxeFreq,
        netAPayer: montProvFreq - montTaxeFreq,
      });
      dateEcheance = prochaine;
    }
    setEcheancier(list);
  };

  const fetchFrais = async (numbdc?: string) => {
    if (!numbdc) { setFrais([]); return; }
    try {
      var params = { CodeAgence: Perss.codage, Table: "FRAISPRELEVEBDC", Colonne1: "NUMBDC", Colonne2: "TYPEFRAIS", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      // Les colonnes de FRAISPRELEVEBDC sont en casse mixte (NumBdc, Montant,
      // Refece...) — normalisées en majuscules avant filtrage/affichage.
      const rows = (normalizeKeysToUppercase(res) || []).filter((f: any) => f.NUMBDC === numbdc);
      setFrais(rows);
    } catch (err) { console.warn('fetchFrais - erreur', err); setFrais([]); }
  };

  // Demande d'interruption du bon (montants et taux appliqués — audit) +
  // paramètres du type pour retrouver les taux utilisés.
  const [demandeInter, setDemandeInter] = useState<any | null>(null);
  const [paramInter, setParamInter] = useState<any | null>(null);
  const fetchInterruption = async (numbdc?: string, typBdc?: string) => {
    setDemandeInter(null); setParamInter(null);
    if (!numbdc) return;
    try {
      var params = { CodeAgence: Perss.codage, Table: "DEMANDEINTERBDC", Colonne1: "NUMDEM", Colonne2: "NUMBDC", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const list = (res || []).filter((d: any) => d.NUMBDC === numbdc);
      // Priorité à la demande validée (02) — sinon la plus récente
      const validee = list.find((d: any) => d.CDETAT === '02');
      setDemandeInter(validee || list[list.length - 1] || null);
    } catch (err) { console.warn('fetchInterruption - erreur', err); }
    if (typBdc) {
      try {
        var params2 = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
        const res2 = await fetchDataTable2(Data.ApiUrl.AnySearch, params2, authHeader());
        setParamInter((res2 || []).find((p: any) => p.CODBDC === typBdc) || null);
      } catch (err) { console.warn('fetchParamBdc - erreur', err); }
    }
  };

  const fetchContrat = async (numbdc?: string) => {
    setContratB64(''); setContratNom('');
    if (!numbdc) return;
    try {
      const response = await axios({
        method: 'get',
        url: Data.ApiUrl.GetContratBDC,
        // L'API attend l'en-tête CypherBonDeCaisse { NUMBDC, CODAGE } — les
        // anciens en-têtes CypherNumBdc/CypherAgence étaient rejetés (400)
        // et le contrat signé ne s'affichait jamais.
        headers: {
          'CypherBonDeCaisse': Crypter.encrypt(JSON.stringify({ NUMBDC: numbdc, CODAGE: Perss.codage })),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        const doc = res?.Result?.Table?.[0];
        if (doc?.PIECEJOINTE) {
          setContratB64(doc.PIECEJOINTE);
          setContratNom(doc.NOMFICHIER || 'contrat.pdf');
        }
      }
    } catch (err) { console.warn('GetContratBDC - erreur', err); }
  };

  // Échéancier réel (ECPIMPUTPROVBDC, généré à la validation) : chaque
  // ligne porte le statut de dotation SOD ('02' = prélevée + DATPRELEV).
  // Retourne false si aucune ligne (bon non validé) -> calcul estimé.
  const fetchEcheancierReel = async (numbdc?: string): Promise<boolean> => {
    if (!numbdc) return false;
    try {
      var params = { CodeAgence: Perss.codage, Table: "ECPIMPUTPROVBDC", Colonne1: "NUMBDC", Colonne2: "DATIMPUT", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const rows = (normalizeKeysToUppercase(res) || [])
        .filter((e: any) => e.NUMBDC === numbdc)
        .sort((a: any, b: any) => (Number(a.NUMSEQ) || 0) - (Number(b.NUMSEQ) || 0));
      if (!rows.length) return false;
      setEcheancier(rows.map((e: any, i: number) => ({
        periode: i + 1,
        dateImput: e.DATIMPUT ? new Date(e.DATIMPUT).toLocaleDateString() : '',
        interet: Number(e.MTNINT) || 0,
        taxe: Number(e.MTNTAXE) || 0,
        netAPayer: (Number(e.MTNINT) || 0) - (Number(e.MTNTAXE) || 0),
        statut: e.CDETAT === '02' ? 'Prélevé' : e.CDETAT === '03' ? 'Annulée' : 'En attente',
        datPrel: e.DATPRELEV ? new Date(e.DATPRELEV).toLocaleDateString() : '',
      })));
      return true;
    } catch (err) { console.warn('fetchEcheancierReel - erreur', err); return false; }
  };

  const selectRow = (row: IBonDeCaisse) => {
    setSelected(row);
    setFicheTab('resume');
    calculerEcheancier(row);          // aperçu immédiat, remplacé par les lignes réelles si elles existent
    fetchEcheancierReel(row.NUMBDC);
    fetchFrais(row.NUMBDC);
    fetchInterruption(row.NUMBDC, row.TYPBDC);
    fetchContrat(row.NUMBDC);
  };

  const annuler = () => {
    setRecherche(''); setEtat(undefined); setTypBdc(undefined);
    setSelected(null); setEcheancier([]); setFrais([]); setContratB64('');
    setDemandeInter(null); setParamInter(null);
  };

  // ------------------------------------------------------------------
  // Exports
  // ------------------------------------------------------------------
  const handleExport = (format: any) => {
    const title = "Exportation bon de caisse";
    switch (format) {
      case 'excel': GlobalExportToExcel(filtered, title, "BonDeCaisse"); break;
      case 'pdf': GlobalExportToPDF(filtered, title, "BonDeCaisse"); break;
      case 'word': GlobalExportToWord(filtered, title, "BonDeCaisse"); break;
      case 'csv': GlobalExportToCSV(filtered, title, "BonDeCaisse"); break;
      case 'json': GlobalExportToJSON(filtered, title, "BonDeCaisse"); break;
      default: break;
    }
  };

  // ------------------------------------------------------------------
  // Colonnes
  // ------------------------------------------------------------------
  const columnsBdc: ColumnsType<IBonDeCaisse> = [
    { title: 'N° BDC', dataIndex: 'NUMBDC', width: 110 },
    { title: 'Réf.', dataIndex: 'REFBDC', width: 100 },
    { title: 'Client', dataIndex: 'NOMCLI', ellipsis: true },
    { title: 'Type', dataIndex: 'TYPBDC', width: 70, align: 'center' },
    { title: 'Montant', dataIndex: 'MONTANT', width: 110, align: 'right', render: (v: any) => formatNumber(Number(v) || 0) },
    { title: 'Durée', dataIndex: 'DUREE', width: 60, align: 'right' },
    { title: 'Date début', dataIndex: 'DATDEBUT', width: 95, render: fmtDate },
    { title: 'Date fin', dataIndex: 'DATFIN', width: 95, render: fmtDate },
    { title: 'État', dataIndex: 'CDETAT', width: 85, align: 'center', render: (s: string) => <EtatTag s={s} /> },
    { title: 'Crée par', dataIndex: 'UTISAI', width: 120, ellipsis: true, render: (v: any) => libUti(v) },
  ];

  const columnsEch: ColumnsType<IEcheance> = [
    { title: 'Période', dataIndex: 'periode', width: 70, align: 'center' },
    { title: 'Date imputation', dataIndex: 'dateImput', width: 110 },
    { title: 'Intérêt', dataIndex: 'interet', width: 120, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Taxe', dataIndex: 'taxe', width: 100, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Net à payer', dataIndex: 'netAPayer', width: 120, align: 'right', render: (v: number) => formatNumber(v) },
    {
      title: 'Statut', dataIndex: 'statut', width: 150, align: 'center',
      render: (_: any, r: IEcheance) => (
        <Tag color={r.statut === 'Prélevé' ? 'green' : r.statut === 'Annulée' ? 'red' : 'orange'}>
          {r.statut || 'En attente'}{r.statut === 'Prélevé' && r.datPrel ? ` le ${r.datPrel}` : ''}
        </Tag>
      ),
    },
  ];

  // Colonnes normalisées en majuscules (normalizeKeysToUppercase)
  const columnsFrais: ColumnsType<any> = [
    { title: 'Type', dataIndex: 'TYPEFRAIS', width: 110 },
    { title: 'Montant', dataIndex: 'MONTANT', width: 100, align: 'right', render: (v: any) => formatNumber(Number(v) || 0) },
    { title: 'Base', dataIndex: 'MONTANTBASE', width: 100, align: 'right', render: (v: any) => formatNumber(Number(v) || 0) },
    { title: 'Compte débité', dataIndex: 'NUMCPT', width: 150 },
    { title: 'Grille', dataIndex: 'CODTARIF', width: 140 },
    { title: 'Palier', dataIndex: 'NUMPALIER', width: 55, align: 'center' },
    { title: 'Réf. écriture', dataIndex: 'REFECE', width: 110 },
    { title: 'Date', dataIndex: 'DATCRE', width: 100, render: fmtDate },
    { title: 'Par', dataIndex: 'UTISAI', width: 100, ellipsis: true, render: (v: any) => libUti(v) },
  ];

  // Détail des retenues / montants appliqués à l'interruption (demande
  // validée — montants calculés par le serveur, taux du ParamBDC)
  const lignesInter = !demandeInter ? [] : [
    { el: 'Intérêts acquis bruts', detail: `${formatNumber(Number(demandeInter.JOURECOULE) || 0)} j écoulés × ${Number(selected?.TXINT) || 0} %/an`, montant: Number(demandeInter.INTACQUISBRUT) || 0 },
    { el: 'Taxe sur intérêts servie', detail: `${Number(selected?.TXTAXE) || 0} % (TXTAXE)`, montant: Number(demandeInter.TAXESERVIE) || 0 },
    { el: 'Intérêts nets conservés', detail: `Mode ${demandeInter.MODEINT || 'NON'}`, montant: Number(demandeInter.INTCONSERVE) || 0 },
    { el: 'Pénalité d\'interruption', detail: `${Number(paramInter?.TXPENALINTERUPT) || 0} %/an × ${formatNumber(Number(demandeInter.JOURRESTANT) || 0)} j restants`, montant: -(Number(demandeInter.PENALITE) || 0) },
    { el: 'Taxe sur pénalité', detail: `${Number(paramInter?.TXTAXEPENALINTERUPT) || 0} % (TXTAXEPENALINTERUPT)`, montant: -(Number(demandeInter.TAXPENAL) || 0) },
    { el: 'Frais d\'interruption', detail: 'Grille du code CODOPEINTERRUP', montant: -(Number(demandeInter.FRAISINT) || 0) },
    { el: 'Net reversé au client', detail: 'Capital + intérêts conservés − retenues', montant: Number(demandeInter.NETCLIENT) || 0 },
  ];

  // ------------------------------------------------------------------
  // Onglets de la fiche
  // ------------------------------------------------------------------
  const tabs = selected ? [
    {
      key: 'resume',
      label: 'Résumé',
      children: (
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="N° BDC">{selected.NUMBDC}</Descriptions.Item>
          <Descriptions.Item label="Référence">{selected.REFBDC}</Descriptions.Item>
          <Descriptions.Item label="Code simul">{selected.CODSIMUL}</Descriptions.Item>
          <Descriptions.Item label="Client">{selected.NUMCLI} - {selected.NOMCLI}</Descriptions.Item>
          <Descriptions.Item label="État"><EtatTag s={selected.CDETAT} /></Descriptions.Item>
          <Descriptions.Item label="Type BDC">{selected.TYPBDC} {typesBdc.find(t => t.CODBDC === selected.TYPBDC)?.LIBELL ? `- ${typesBdc.find(t => t.CODBDC === selected.TYPBDC)?.LIBELL}` : ''}</Descriptions.Item>
          <Descriptions.Item label="Type imputation">{selected.TYPIMPUTINT === 'PRE' ? 'Précompté' : selected.TYPIMPUTINT === 'POS' ? 'Postcompté' : selected.TYPIMPUTINT}</Descriptions.Item>
          <Descriptions.Item label="Fréquence">{selected.FREQPROV}</Descriptions.Item>
          <Descriptions.Item label="Durée">{selected.DUREE}</Descriptions.Item>
          <Descriptions.Item label="Montant">{formatNumber(Number(selected.MONTANT) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Taux intérêt">{selected.TXINT} %</Descriptions.Item>
          <Descriptions.Item label="Taux taxe">{selected.TXTAXE} %</Descriptions.Item>
          <Descriptions.Item label="Date début">{fmtDate(selected.DATDEBUT)}</Descriptions.Item>
          <Descriptions.Item label="Date fin">{fmtDate(selected.DATFIN)}</Descriptions.Item>
          <Descriptions.Item label="Intérêts bruts">{formatNumber(Number(selected.MTNINT) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Taxe retenue">{formatNumber(Number(selected.MTNTAXE) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Net à payer">{formatNumber(Number(selected.MTNNETAPAYER) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Compte dépôt">{selected.CPTDEPOT}</Descriptions.Item>
          <Descriptions.Item label="Compte BDC">{selected.CPTBDC}</Descriptions.Item>
          <Descriptions.Item label="Compte intérêts">{selected.CPTINTCLI}</Descriptions.Item>
          <Descriptions.Item label="Code opération">{selected.CODOPE}</Descriptions.Item>
          <Descriptions.Item label="Créé par">{libUti(selected.UTISAI)}</Descriptions.Item>
          <Descriptions.Item label="Créé le">{fmtDateTime(selected.DATCRE)}</Descriptions.Item>
          <Descriptions.Item label="Validé par">{libUti(selected.UTIVAL)}</Descriptions.Item>
          <Descriptions.Item label="Validé le">{fmtDateTime(selected.DATVAL)}</Descriptions.Item>
          <Descriptions.Item label="Agence">{selected.CODAGE}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'echeancier',
      label: `Échéancier (${echeancier.length})`,
      children: (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Intérêts bruts {formatNumber(totalInteret)} — Taxe {formatNumber(totalTaxe)} — Net {formatNumber(totalNet)}
          </Typography>
          <Table size="small" rowKey="periode" columns={columnsEch} dataSource={echeancier} pagination={false} scroll={{ y: 260 }} />
        </>
      ),
    },
    {
      key: 'frais',
      label: `Frais${demandeInter ? ' / interruption' : ''} (${demandeInter ? lignesInter.length : frais.length})`,
      children: demandeInter ? (
        <>
          <Typography color={"#116599"} sx={{ mb: 1 }}>
            <b>Interruption {demandeInter.NUMDEM} — demandée le {fmtDate(demandeInter.DATDEM)} par {libUti(demandeInter.UTIDEM)}
              {demandeInter.CDETAT === '02' ? `, validée le ${fmtDate(demandeInter.DATVAL)} par ${libUti(demandeInter.UTIVAL)}` : ''}</b>
          </Typography>
          <Table size="small" rowKey="el" pagination={false} dataSource={lignesInter} columns={[
            { title: 'Élément', dataIndex: 'el', width: '35%' },
            { title: 'Taux / base appliquée', dataIndex: 'detail' },
            { title: 'Montant', dataIndex: 'montant', width: 140, align: 'right', render: (v: number) => formatNumber(v) },
          ]} />
        </>
      ) : (
        <Table size="small" rowKey={(r: any) => String(r.IDFRAIS ?? Math.random())} columns={columnsFrais} dataSource={frais} pagination={false} locale={{ emptyText: 'Aucun frais prélevé ni interruption pour ce bon' }} />
      ),
    },
    {
      key: 'contrat',
      label: 'Contrat signé',
      children: contratB64 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => setContratPreview(true)}
            style={{ width: 92, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff5f5', cursor: 'pointer', position: 'relative' }}
          >
            <FilePdfOutlined style={{ fontSize: 28, color: '#d4380d' }} />
            <EyeOutlined style={{ position: 'absolute', top: 4, left: 4, color: 'var(--darkColor, #116599)', fontSize: 12 }} />
          </div>
          <div>
            <b>{contratNom}</b>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Cliquez pour visualiser le contrat signé</div>
          </div>
        </div>
      ) : (
        <Typography variant="body2" color="textSecondary">Aucun contrat signé chargé pour ce bon.</Typography>
      ),
    },
  ] : [];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'> <b> VISUALISATION BON DE CAISSE </b> </Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign='left' form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' onClick={() => { fetchData(); }} type="ghost" htmlType="button" block  >
                  Actualiser
                </Button>
                <ExportMenu disabled={filtered.length === 0} onExport={handleExport} />
                <Button size='large' onClick={annuler} className='bt_add' type="ghost" htmlType="reset" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div>
              <Tabs size='small' type="card" defaultActiveKey="1" items={[{
                key: '1',
                label: <span id='Tab'><EyeOutlined /> Visualisation</span>,
                children: (
                  <div className='tabItem' style={{ height: '780px', display: 'block' }}>
                    <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>

                      <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                        <legend>Critères de recherche</legend>
                        <Space wrap>
                          <Input.Search
                            allowClear
                            placeholder="N° BDC, client, compte, référence, simul..."
                            style={{ width: 300 }}
                            value={recherche}
                            onChange={(e) => setRecherche(e.target.value)}
                          />
                          <Select
                            allowClear
                            placeholder="Tous les états"
                            style={{ minWidth: 150 }}
                            value={etat}
                            onChange={(v) => { setEtat(v); setSelected(null); }}
                            options={Object.entries(ETAT_LABEL).map(([v, l]) => ({ value: v, label: l }))}
                          />
                          <Select
                            allowClear
                            placeholder="Tous les types"
                            style={{ minWidth: 220 }}
                            value={typBdc}
                            onChange={(v) => { setTypBdc(v); setSelected(null); }}
                            options={typesBdc.map((t) => ({ value: t.CODBDC, label: `${t.CODBDC} - ${t.LIBELL}` }))}
                          />
                        </Space>
                      </Box>

                      <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                        <legend>Bons de caisse ({filtered.length})</legend>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Sélectionnez une ligne pour afficher la fiche</Typography>
                        <Table
                          size="small"
                          rowKey={(r: IBonDeCaisse) => String(r.NUMBDC)}
                          columns={columnsBdc}
                          dataSource={filtered}
                          rowSelection={{ type: 'radio', selectedRowKeys: selected ? [String(selected.NUMBDC)] : [], onChange: (_, rows) => { if (rows[0]) selectRow(rows[0]); } }}
                          onRow={(r) => ({ onClick: () => selectRow(r), style: { cursor: 'pointer' } })}
                          pagination={{ pageSize: 8, showSizeChanger: false }}
                          scroll={{ x: 900 }}
                        />
                      </Box>

                      {selected && (
                        <Box fontStyle="inherit" className="border border-left" sx={{ p: 2 }}>
                          <legend>
                            Fiche bon de caisse {selected.NUMBDC} — {selected.NOMCLI} <EtatTag s={selected.CDETAT} />
                          </legend>
                          <Tabs size="small" type="card" activeKey={ficheTab} onChange={setFicheTab} items={tabs} />
                        </Box>
                      )}
                    </div>
                  </div>
                ),
              }]} />
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

      {/* Prévisualisation du contrat signé */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            {contratNom || 'Aperçu du contrat'}
          </div>
        }
        open={contratPreview}
        onCancel={() => setContratPreview(false)}
        footer={null}
        width="90%"
        centered
        zIndex={2100}
        style={{ maxWidth: '1400px' }}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
      >
        {contratB64 && (
          <iframe
            src={contratB64}
            title="Aperçu contrat"
            style={{ width: '100%', height: '75vh', border: '1px solid var(--colorOpaque030)', borderRadius: 10 }}
          />
        )}
      </Modal>
    </div>
  );
}
